import type { MouseEvent, Ref } from 'react';
import { createContext, useContext, useRef } from 'react';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import type { GroupProps as AriaGroupProps } from 'react-aria-components/Group';
import { Group as AriaGroup } from 'react-aria-components/Group';
import type { ContextValue } from 'react-aria-components/slots';
import { cva, cx } from 'class-variance-authority';

import type { InputVariants } from './Input';
import { inputStyles } from './Input';
import { useLPContextProps } from './utils';

import styles from './styles/Group.module.css';

const groupStyles = cva(styles.group);

const INTERACTIVE_SELECTOR = [
	'input',
	'textarea',
	'select',
	'button',
	'a[href]',
	'[contenteditable="true"]',
	'[tabindex]',
	'[role="button"]',
	'[role="spinbutton"]',
	'[role="row"]',
	'[role="gridcell"]',
	'[role="option"]',
].join(', ');

const ENTRY_SELECTOR = [
	'input:not([disabled]):not([type="hidden"])',
	'textarea:not([disabled])',
	'[role="spinbutton"]:not([aria-disabled="true"])',
].join(', ');

const getGroupEntryTarget = (event: MouseEvent<HTMLElement>) => {
	const group = event.currentTarget;
	if (event.button !== 0 || !(event.target instanceof Element)) {
		return null;
	}
	const interactive = event.target.closest(INTERACTIVE_SELECTOR);
	if (interactive && interactive !== group && group.contains(interactive)) {
		return null;
	}
	return group.querySelector<HTMLElement>(ENTRY_SELECTOR);
};

interface GroupProps extends AriaGroupProps, InputVariants {
	ref?: Ref<HTMLDivElement>;
}

const GroupContext = createContext<ContextValue<GroupProps, HTMLDivElement>>(null);

const GroupEntryPressContext = createContext<(() => void) | null>(null);

/**
 * A group represents a set of related UI controls, and supports interactive states for styling.
 *
 * Pressing the group's padding, gaps, or decorative children focuses its first text entry, so the whole visible field
 * behaves like the input. Mouse and pen presses focus on pointer down; touch presses focus on click so mobile browsers
 * open the on-screen keyboard. Calling `preventDefault()` in `onPointerDown` opts out.
 *
 * https://react-spectrum.adobe.com/react-aria/Group.html
 */
const Group = ({ ref, ...props }: GroupProps) => {
	const [mergedProps, mergedRef] = useLPContextProps(props, ref, GroupContext);
	const { variant = 'default', isDisabled, onPointerDown, onClick } = mergedProps;
	const onEntryPress = useContext(GroupEntryPressContext);
	const touchPressRef = useRef(false);

	const pressEntry = (event: MouseEvent<HTMLElement>) => {
		const entry = isDisabled ? null : getGroupEntryTarget(event);
		if (entry) {
			event.preventDefault();
			entry.focus();
			onEntryPress?.();
		}
	};

	return (
		<AriaGroup
			{...mergedProps}
			ref={mergedRef}
			onPointerDown={(event) => {
				onPointerDown?.(event);
				touchPressRef.current = event.pointerType === 'touch' && !event.defaultPrevented;
				if (event.pointerType !== 'touch' && !event.defaultPrevented) {
					pressEntry(event);
				}
			}}
			onClick={(event) => {
				onClick?.(event);
				if (touchPressRef.current && !event.defaultPrevented) {
					pressEntry(event);
				}
				touchPressRef.current = false;
			}}
			className={composeRenderProps(mergedProps.className, (className, renderProps) =>
				cx(inputStyles({ variant }), groupStyles({ ...renderProps, className })),
			)}
		/>
	);
};

export { Group, GroupContext, GroupEntryPressContext, groupStyles };
export type { GroupProps };
